/**
 * The per-building weekly report, as a data model.
 *
 * Pure and React-free. It returns the SAME SHAPE that
 * `fleet_report.build_building_report` returns in cts_app_services, because the
 * emailed report and this page have to be two renderings of one document. A
 * site manager reads the email, clicks through, and has to find the number they
 * just read. That only stays true if the two are the same computation, so the
 * counting comes from `reportCounts`, which is held to the same
 * `req-count-golden.json` as the Python and the C#.
 *
 * WHY THIS IS DERIVED IN THE BROWSER: the sensors table already walks every
 * sensor's alarms, sessions and connectivity to build its rows. The API's
 * `reports/buildings/{groupId}/summary` covers one window and injects no
 * connectivity repository, so it can produce neither the comparison nor the
 * equipment section. Deriving here means the page works with none of the
 * report infrastructure deployed, and the shape above is the seam that lets the
 * source be swapped later without touching the page.
 */

import {
  countAlertTransitions,
  countSessions,
  parseTimestamp,
} from "./reportCounts";

/** Mirrors fleet_window's section vocabulary. */
export const SECTION_ACTIVITY = "activity";
export const SECTION_ALERTS = "alerts";
export const SECTION_EQUIPMENT = "equipment";

export const DEFAULT_SECTIONS = [
  SECTION_ACTIVITY,
  SECTION_ALERTS,
  SECTION_EQUIPMENT,
] as const;

/** Mirrors fleet_report's KIND_ bases, so the two rank identically. */
export const KIND_SHUTOFFS = 400;
export const KIND_ALERTS = 300;
export const KIND_OFFLINE = 100;

export type UnitInput = {
  scsName: string;
  name: string;
  alarms: unknown[];
  sessions: unknown[];
  /** Connectivity rows covering the window, newest-first or any order. */
  connectivity: unknown[] | null;
  /**
   * Connectivity rows for the "online now" question only, UNCLIPPED by the
   * sensor's current-location cutoff. Falls back to `connectivity`.
   *
   * Why the two differ: `connectivity` is clipped to the sensor's current
   * location, because outage hours it racked up at a previous address are not
   * this address's outage. But "online now" is a live-state question, and a
   * sensor re-installed an hour ago has no post-install connectivity rows yet --
   * reading the clipped array would report a perfectly healthy sensor as offline
   * and raise an equipment issue on a safety report. So the live question reads
   * every row we have.
   */
  connectivityLive?: unknown[] | null;
  /** Alarms and sessions over the preceding period, or null for no comparison. */
  prior: { alarms: unknown[]; sessions: unknown[] } | null;
};

export type ReportWindow = {
  /** Epoch millis, both bounds inclusive. */
  start: number;
  end: number;
  priorStart: number;
  priorEnd: number;
  label: string;
};

export type Issue = { kind: string; severity: number; text: string };

export type UnitReport = {
  scsName: string;
  name: string;
  sessions: number;
  alerts: number;
  shutoffs: number;
  online: boolean;
  /** Hours disconnected during the window, or null for unknown. */
  offlineHours: number | null;
  // Deliberately no lastPresenceAt. Presence is the wellness question, asked of
  // a different reader by a different feature, and this report does not ask it
  // (REQ-BLDG-3). A field that was always null would invite someone to fill it.
  previous: { sessions: number; alerts: number; shutoffs: number } | null;
  delta: { sessions: number; alerts: number; shutoffs: number } | null;
};

export type BuildingReport = {
  kind: "building";
  buildingId: string;
  buildingName: string;
  periodLabel: string;
  sections: string[];
  totals: {
    sensors: number;
    sessions: number;
    alerts: number;
    shutoffs: number;
    offline: number;
    offlineHours: number | null;
    offlineHoursUnknown: number;
  };
  attention: { scsName: string; name: string; severity: number; issues: Issue[] }[];
  units: UnitReport[];
  previous: { sessions: number; alerts: number; shutoffs: number } | null;
  delta: { sessions: number; alerts: number; shutoffs: number } | null;
};

const HOUR_MS = 3_600_000;

function plural(count: number, singular: string, other?: string): string {
  return count === 1 ? singular : other || `${singular}s`;
}

/**
 * Hours as a reader would say them. Mirrors `fleet_report.hours_text`, so the
 * page and the email describe one outage the same way.
 */
export function hoursText(hours: number): string {
  if (hours < 1) return "under an hour";
  const whole = Math.round(hours);
  if (whole < 48) return `${whole} ${plural(whole, "hour")}`;
  const days = Math.round(hours / 24);
  return `about ${days} ${plural(days, "day")}`;
}

/**
 * Hours this sensor spent disconnected during the window.
 *
 * Walks the connectivity transitions rather than reading the newest row, which
 * would answer "offline right now": a sensor down Tuesday through Thursday and
 * back up Friday reads as perfectly healthy in a weekly report.
 *
 * Returns null when there is no evidence at all, which renders as unknown.
 * Reporting an unmonitored week as a clean one is the failure this product
 * cannot afford, so a gap in the record is never rounded down to zero. An
 * unknown state entering the window counts as offline, failing closed the same
 * way the server does.
 */
export function offlineHours(
  rows: unknown[] | null | undefined,
  start: number,
  end: number,
  connectedAtStart: boolean | null
): number | null {
  const events: { at: number; connected: boolean }[] = [];

  for (const row of rows || []) {
    const record = row as { datetime?: string; eventType?: string };
    const at = parseTimestamp(record?.datetime);
    if (at === null || at < start || at > end) continue;
    events.push({
      at,
      connected: String(record?.eventType || "").toLowerCase() === "connected",
    });
  }

  if (events.length === 0 && connectedAtStart === null) return null;

  events.sort((a, b) => a.at - b.at);

  let connected = connectedAtStart === true;
  let offline = 0;
  let cursor = start;

  for (const event of events) {
    if (!connected) offline += event.at - cursor;
    connected = event.connected;
    cursor = event.at;
  }

  if (!connected) offline += end - cursor;

  return Math.round((offline / HOUR_MS) * 10) / 10;
}

function periodCounts(
  alarms: unknown[],
  sessions: unknown[],
  start: number,
  end: number
) {
  const { danger, shutoffs } = countAlertTransitions(alarms as never, start, end);
  return {
    sessions: countSessions(sessions as never, start, end),
    alerts: danger,
    shutoffs,
  };
}

export function summariseUnit(
  unit: UnitInput,
  window: ReportWindow,
  sections: readonly string[]
): UnitReport {
  const counts = periodCounts(unit.alarms, unit.sessions, window.start, window.end);

  const measuring = sections.includes(SECTION_EQUIPMENT) && unit.connectivity !== null;

  // The state entering the window is the newest transition at or before it.
  // Null means no record before the window at all, which is unknown, not online.
  let connectedAtStart: boolean | null = null;
  let newestBefore = -Infinity;
  for (const row of unit.connectivity || []) {
    const record = row as { datetime?: string; eventType?: string };
    const at = parseTimestamp(record?.datetime);
    if (at === null || at > window.start || at <= newestBefore) continue;
    newestBefore = at;
    connectedAtStart =
      String(record?.eventType || "").toLowerCase() === "connected";
  }

  // "Online now" is the newest transition on record, with no window bound --
  // this is a live-state question, so it reads `connectivityLive` (see the type)
  // rather than the location-clipped rows the outage math above uses. Unknown
  // reads as NOT online, so an ambiguous sensor looks like something to check.
  let online = false;
  let newest = -Infinity;
  for (const row of unit.connectivityLive ?? unit.connectivity ?? []) {
    const record = row as { datetime?: string; eventType?: string };
    const at = parseTimestamp(record?.datetime);
    if (at === null || at <= newest) continue;
    newest = at;
    online = String(record?.eventType || "").toLowerCase() === "connected";
  }
  if (!measuring) {
    // Not asked for, so not claimed either way, and no offline column renders.
    online = true;
  }

  const previous = unit.prior
    ? periodCounts(
        unit.prior.alarms,
        unit.prior.sessions,
        window.priorStart,
        window.priorEnd
      )
    : null;

  return {
    scsName: unit.scsName,
    name: unit.name,
    sessions: counts.sessions,
    alerts: counts.alerts,
    shutoffs: counts.shutoffs,
    online,
    offlineHours: measuring
      ? offlineHours(unit.connectivity, window.start, window.end, connectedAtStart)
      : null,
    previous,
    // Omitted, not zeroed, when there is no prior period: "0 last week" about a
    // sensor installed on Thursday is a claim.
    delta: previous
      ? {
          sessions: counts.sessions - previous.sessions,
          alerts: counts.alerts - previous.alerts,
          shutoffs: counts.shutoffs - previous.shutoffs,
        }
      : null,
  };
}

/**
 * The reasons one unit is worth a site manager's attention.
 *
 * Deliberately the same KIND_ bases the emailed report uses. A site manager and
 * a portfolio manager must not see the same situation ranked two different
 * ways. The week-over-week delta is NOT here and must never be: it is a column,
 * not a rank.
 */
export function issuesForUnit(
  unit: UnitReport,
  sections: readonly string[]
): Issue[] {
  const issues: Issue[] = [];

  if (sections.includes(SECTION_ALERTS) && unit.shutoffs > 0) {
    issues.push({
      kind: "shutoffs",
      severity: KIND_SHUTOFFS + unit.shutoffs,
      text: `${unit.shutoffs} safety ${plural(unit.shutoffs, "shut-off")}`,
    });
  }

  if (sections.includes(SECTION_ALERTS) && unit.alerts > 0) {
    issues.push({
      kind: "alerts",
      severity: KIND_ALERTS + unit.alerts,
      text: `${unit.alerts} ${plural(unit.alerts, "alert")}`,
    });
  }

  if (sections.includes(SECTION_EQUIPMENT) && !unit.online) {
    const hours = unit.offlineHours;
    issues.push({
      kind: "offline",
      severity: KIND_OFFLINE + Math.round(hours || 0),
      text: hours === null ? "offline" : `offline, ${hoursText(hours)} this period`,
    });
  }

  return issues;
}

export function buildBuildingReport(
  buildingId: string,
  buildingName: string,
  units: UnitInput[],
  window: ReportWindow,
  sections: readonly string[] = DEFAULT_SECTIONS
): BuildingReport {
  const summaries = units
    .map((unit) => summariseUnit(unit, window, sections))
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  const attention = summaries
    .map((unit) => ({ unit, issues: issuesForUnit(unit, sections) }))
    .filter(({ issues }) => issues.length > 0)
    .map(({ unit, issues }) => ({
      scsName: unit.scsName,
      name: unit.name,
      severity: Math.max(...issues.map((i) => i.severity)),
      issues: issues.sort((a, b) => b.severity - a.severity),
    }))
    // Worst first, then by name so two equally urgent units do not swap places
    // between two renders of the same report.
    .sort(
      (a, b) =>
        b.severity - a.severity ||
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );

  const sum = (pick: (u: UnitReport) => number) =>
    summaries.reduce((total, unit) => total + pick(unit), 0);

  // Summed only over the units that actually have a measurement: treating an
  // unknown as zero would let one unmonitored sensor make the building look
  // better than the sensors we can see justify.
  const measured = summaries
    .map((u) => u.offlineHours)
    .filter((h): h is number => h !== null);

  const compared = summaries.filter((u) => u.previous !== null);
  const total = (key: "sessions" | "alerts" | "shutoffs") =>
    compared.reduce((t, u) => t + (u.previous as UnitReport["previous"])![key], 0);

  return {
    kind: "building",
    buildingId,
    buildingName,
    periodLabel: window.label,
    sections: [...sections],
    totals: {
      sensors: summaries.length,
      sessions: sum((u) => u.sessions),
      alerts: sum((u) => u.alerts),
      shutoffs: sum((u) => u.shutoffs),
      offline: summaries.filter((u) => !u.online).length,
      offlineHours:
        measured.length > 0
          ? Math.round(measured.reduce((a, b) => a + b, 0) * 10) / 10
          : null,
      offlineHoursUnknown: summaries.filter((u) => u.offlineHours === null).length,
    },
    attention,
    units: summaries,
    previous:
      compared.length > 0
        ? {
            sessions: total("sessions"),
            alerts: total("alerts"),
            shutoffs: total("shutoffs"),
          }
        : null,
    delta:
      compared.length > 0
        ? {
            sessions: sum((u) => u.sessions) - total("sessions"),
            alerts: sum((u) => u.alerts) - total("alerts"),
            shutoffs: sum((u) => u.shutoffs) - total("shutoffs"),
          }
        : null,
  };
}

/**
 * The seven complete local days ending at midnight this morning, and the seven
 * before them.
 *
 * Matches `fleet_window.report_window`: a period that stopped at the moment of
 * rendering would make two views of the same report disagree. The comparison
 * period ends one millisecond earlier so an event on local midnight cannot be
 * counted in both halves.
 */
export function weeklyWindow(now: Date = new Date()): ReportWindow {
  const endDate = new Date(now);
  endDate.setHours(0, 0, 0, 0);

  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - 7);

  const priorStartDate = new Date(startDate);
  priorStartDate.setDate(priorStartDate.getDate() - 7);

  const end = endDate.getTime();
  const start = startDate.getTime();

  return {
    start,
    end,
    priorStart: priorStartDate.getTime(),
    priorEnd: start - 1,
    label: formatPeriod(startDate, endDate),
  };
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * "Aug 1 to 7, 2026". The end is exclusive internally but inclusive to a
 * reader: nobody thinks of a week as ending on the Monday after it.
 */
export function formatPeriod(start: Date, end: Date): string {
  const last = new Date(end);
  last.setDate(last.getDate() - 1);

  const startMonth = MONTHS[start.getMonth()];
  const lastMonth = MONTHS[last.getMonth()];

  if (start.getFullYear() !== last.getFullYear()) {
    return `${startMonth} ${start.getDate()}, ${start.getFullYear()} to ` +
      `${lastMonth} ${last.getDate()}, ${last.getFullYear()}`;
  }
  if (start.getMonth() !== last.getMonth()) {
    return `${startMonth} ${start.getDate()} to ` +
      `${lastMonth} ${last.getDate()}, ${last.getFullYear()}`;
  }
  return `${startMonth} ${start.getDate()} to ${last.getDate()}, ${last.getFullYear()}`;
}

/** "+3", "-2" or "no change". Never a percentage: a rise from zero has none. */
export function changeText(delta: number): string {
  return delta === 0 ? "no change" : `${delta > 0 ? "+" : ""}${delta}`;
}
